import React, { useState, useMemo } from 'react';
import {
  useGetAllUsersHolidayStatsQuery,
  useGetHolidayRequestsQuery,
  HOLIDAY_FETCH_PAGE_SIZE,
} from '../../../store/slices/apiSlice';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import { EmptyState } from '../../../components/common/EmptyState';
import { UserHolidayDetailModal } from '../components/UserHolidayDetailModal';

interface UserSummary {
  user_id: number;
  username: string;
  first_name: string;
  last_name: string;
  allocated: number;
  taken: number;
  remaining: number;
}

export const OverviewTab: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedUser, setSelectedUser] = useState<UserSummary | null>(null);

  const {
    data: statsData,
    isLoading: isStatsLoading,
  } = useGetAllUsersHolidayStatsQuery({ year: selectedYear });

  const {
    data: userRequestsData,
    isLoading: isUserRequestsLoading,
  } = useGetHolidayRequestsQuery(
    {
      year: selectedYear,
      user_id: selectedUser?.user_id,
      status: 'approved',
      page_size: HOLIDAY_FETCH_PAGE_SIZE,
    },
    { skip: !selectedUser }
  );

  const usersWithHolidays = useMemo(() => {
    if (!statsData?.results) return [];
    return statsData.results.filter((user: UserSummary) => user.taken > 0);
  }, [statsData]);

  const userApprovedRequests = userRequestsData?.results ?? [];

  const yearOptions = useMemo(() => {
    const years = [];
    for (let i = currentYear - 1; i <= currentYear + 2; i++) {
      years.push(i);
    }
    return years;
  }, [currentYear]);

  const isLoading = isStatsLoading;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Team Holiday Overview</h3>
            <p className="text-sm text-gray-500 mt-1">
              View approved holidays for all team members
            </p>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Year:</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="form-input w-32"
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h4 className="font-semibold text-gray-900">
            Users with Approved Holidays
          </h4>
        </div>

        <div className="p-6">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : usersWithHolidays.length === 0 ? (
            <EmptyState
              title="No approved holidays"
              description={`No users have approved holidays for ${selectedYear}.`}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Username
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Allocated
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Taken
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Left
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {usersWithHolidays.map((user) => (
                    <tr
                      key={user.user_id}
                      className="hover:bg-blue-50 cursor-pointer transition-colors"
                      onClick={() => setSelectedUser(user)}
                    >
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-medium text-sm">
                            {user.first_name?.[0]}{user.last_name?.[0]}
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">
                              {user.first_name} {user.last_name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-600">@{user.username}</span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">{Number(user.allocated).toFixed(0)}</span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-green-600">{Number(user.taken).toFixed(2).replace(/\.00$/, '')}</span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`text-sm font-medium ${
                          user.remaining <= 0 ? 'text-red-600' : 'text-gray-900'
                        }`}>
                          {Number(user.remaining).toFixed(2).replace(/\.00$/, '')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!isLoading && usersWithHolidays.length > 0 && (
            <p className="text-xs text-gray-500 mt-4 text-center">
              Click on a row to view detailed approved holidays for that user
            </p>
          )}
        </div>
      </div>

      <UserHolidayDetailModal
        isOpen={!!selectedUser}
        onClose={() => setSelectedUser(null)}
        userName={selectedUser ? `${selectedUser.first_name} ${selectedUser.last_name}` : ''}
        userUsername={selectedUser?.username || ''}
        requests={userApprovedRequests}
        isLoading={isUserRequestsLoading}
        year={selectedYear}
        takenDaysFromStats={selectedUser?.taken}
      />
    </div>
  );
};

export default OverviewTab;
